import { downloadInvoice, getBillingDetails, getBillingPayments, getPaymentMethod } from "api/billing";
import { ApiError } from "api/client";
import { cancelSubscription, getSubscriptionStatus } from "api/subscriptions";
import type { BillingDetailsDto, BillingPaymentDto, PaymentMethodDto, SubscriptionStatusDto } from "api/types";
import BillingDetailsDialog from "pages/PlansPage/BillingDetailsDialog";
import TopNav from "pages/TreeViewPage/TopNav";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Paths from "routes/Paths";
import { useSession } from "Session";

import { BillingPanel, GeneralPanel, NotificationsPanel, SettingsNav } from "./components";

import type { SettingsNavItem, SettingsSection } from "./settings.types";

import styles from "./SettingsPage.module.css";

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "U";

/**
 * Account settings. Currently exposes two panes — General (profile/account) and Billing
 * (Pro subscription + saved billing address). Deeper plan management stays on the plans page.
 */
const SettingsPage = () => {
	const { t } = useTranslation("settingsPage");
	const navigate = useNavigate();
	const { getAccessToken, signOut, user } = useSession();
	const [activeSection, setActiveSection] = useState<SettingsSection>("general");
	const [subscription, setSubscription] = useState<SubscriptionStatusDto | null>(null);
	const [billingDetails, setBillingDetails] = useState<BillingDetailsDto | null>(null);
	const [payments, setPayments] = useState<BillingPaymentDto[]>([]);
	const [paymentMethod, setPaymentMethod] = useState<PaymentMethodDto | null>(null);
	const [downloadingPaymentId, setDownloadingPaymentId] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isCancelling, setIsCancelling] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [isBillingDialogOpen, setIsBillingDialogOpen] = useState(false);

	const displayName = user?.fullName ?? t("common:yourAccount");
	const email = user?.email ?? "";
	const initials = getInitials(user?.fullName ?? "");
	const emailConfirmed = user?.emailConfirmed ?? false;

	const navItems = useMemo<SettingsNavItem[]>(
		() => [
			{ icon: "settings", id: "general", label: t("nav.general") },
			{ icon: "bell", id: "notifications", label: t("nav.notifications") },
			{ icon: "card", id: "billing", label: t("nav.billing") }
		],
		[t]
	);

	// Billing details drive the Billing pane; load them up front so switching panes is instant.
	useEffect(() => {
		let isCurrent = true;

		const load = async (): Promise<void> => {
			try {
				const token = await getAccessToken();
				if (!token || !isCurrent) {
					return;
				}

				// Payment history and the instrument on file are auxiliary: if either fetch fails (e.g.
				// the API is older than these endpoints, or Razorpay hiccups), their sections simply
				// hide — a failure must never discard the subscription status alongside it.
				const [status, billing, paymentList, method] = await Promise.all([
					getSubscriptionStatus(token),
					getBillingDetails(token),
					getBillingPayments(token).catch(() => []),
					getPaymentMethod(token).catch(() => null)
				]);

				if (!isCurrent) {
					return;
				}

				setSubscription(status);
				setBillingDetails(billing);
				setPayments(paymentList);
				setPaymentMethod(method);
			} catch (error) {
				if (isCurrent && error instanceof ApiError && error.status === 401) {
					signOut();
				}
			} finally {
				if (isCurrent) {
					setIsLoading(false);
				}
			}
		};

		load().catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, [getAccessToken, signOut]);

	const onManagePlan = useCallback((): void => navigate(Paths.plans), [navigate]);
	const onEditBillingAddress = useCallback((): void => setIsBillingDialogOpen(true), []);
	const onCloseBillingDialog = useCallback((): void => setIsBillingDialogOpen(false), []);
	const onBillingSaved = useCallback((saved: BillingDetailsDto): void => {
		setBillingDetails(saved);
		setIsBillingDialogOpen(false);
	}, []);

	const onDownloadInvoice = useCallback(
		async (paymentId: string): Promise<void> => {
			if (downloadingPaymentId) {
				return;
			}

			setErrorMessage("");
			setDownloadingPaymentId(paymentId);
			try {
				const token = await getAccessToken();
				if (!token) {
					return;
				}

				await downloadInvoice(paymentId, token);
			} catch (error) {
				setErrorMessage(error instanceof Error ? error.message : t("billing.genericError"));
			} finally {
				setDownloadingPaymentId(null);
			}
		},
		[downloadingPaymentId, getAccessToken, t]
	);

	const onCancelAutoRenew = useCallback(async (): Promise<void> => {
		// eslint-disable-next-line no-alert
		if (isCancelling || !window.confirm(t("billing.confirmCancel"))) {
			return;
		}

		setErrorMessage("");
		setIsCancelling(true);
		try {
			const token = await getAccessToken();
			if (!token) {
				return;
			}

			const status = await cancelSubscription(token);
			setSubscription(status);
		} catch (error) {
			setErrorMessage(error instanceof Error ? error.message : t("billing.genericError"));
		} finally {
			setIsCancelling(false);
		}
	}, [getAccessToken, isCancelling, t]);

	const headings: Record<SettingsSection, string> = {
		billing: t("billing.heading"),
		general: t("general.heading"),
		notifications: t("notifications.heading")
	};
	const heading = headings[activeSection];

	return (
		<div className={styles.page}>
			<TopNav />

			<div className={styles.body}>
				<aside className={styles.sidebar}>
					<SettingsNav activeSection={activeSection} items={navItems} onSelect={setActiveSection} />
				</aside>

				<main className={styles.content}>
					<header className={styles.header}>
						<h1 className={styles.heading}>{heading}</h1>
					</header>

					{activeSection === "general" && (
						<GeneralPanel
							displayName={displayName}
							email={email}
							emailConfirmed={emailConfirmed}
							initials={initials}
							onSignOut={signOut}
							showEmailOnProfile={user?.showEmailOnProfile ?? false}
						/>
					)}

					{activeSection === "notifications" && <NotificationsPanel />}

					{activeSection === "billing" && (
						<BillingPanel
							billingDetails={billingDetails}
							downloadingPaymentId={downloadingPaymentId}
							errorMessage={errorMessage}
							isCancelling={isCancelling}
							isLoading={isLoading}
							onCancelAutoRenew={onCancelAutoRenew}
							onDownloadInvoice={onDownloadInvoice}
							onEditBillingAddress={onEditBillingAddress}
							onManagePlan={onManagePlan}
							onUpgrade={onManagePlan}
							paymentMethod={paymentMethod}
							payments={payments}
							subscription={subscription}
						/>
					)}
				</main>
			</div>

			{isBillingDialogOpen && (
				<BillingDetailsDialog
					defaultCustomerName={displayName}
					initialDetails={billingDetails}
					onClose={onCloseBillingDialog}
					onSaved={onBillingSaved}
				/>
			)}
		</div>
	);
};

export default SettingsPage;
